import React, { useEffect, useRef, useState } from "react";
import api from "../api";
import {
  Bot, Send, X, Sparkles, MessageSquare, RefreshCw, AlertCircle, CheckCircle2, Loader2,
} from "lucide-react";

/**
 * Turbo AI Support — Step 13
 * Floating chat launcher docked to the merchant dashboard. Serves TF-IDF+cosine
 * answers from /api/tgc/support/ask. Self-learning: low-confidence questions
 * silently stage in the admin queue.
 *
 * The launcher auto-detects the browser locale (Accept-Language) and forwards
 * it as `lang` — the RAG core returns answers in the matching language when
 * available.
 */
export default function TurboAiSupport() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    const seed = navigator.language?.startsWith("ru")
      ? "Привет! Я Turbo AI Support. Спросите о комиссиях, Web3, налогах, зарплате или AIFC-комплаенсе."
      : "Hi! I'm Turbo AI Support. Ask me about fees, Web3, tax, payroll, or AIFC compliance.";
    return [{ role: "bot", text: seed, ts: Date.now() }];
  });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const [accountType, setAccountType] = useState(null);
  const scrollRef = useRef(null);
  const lang = (navigator.language || "en").slice(0, 2);

  useEffect(() => {
    if (!open) return;
    api.get("/support/kb/stats").then((r) => setStats(r.data)).catch(() => {});
    // STEP 20 — detect account_type so merchant-scoped chips only render for
    // MERCHANT_RETAIL / MERCHANT_ENTERPRISE tiers.
    api.get("/auth/me").then((r) => setAccountType(r.data?.account_type || null)).catch(() => {});
  }, [open]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (override) => {
    const q = (override ?? input).trim();
    if (!q || busy) return;
    if (!override) setInput("");
    setMessages((m) => [...m, { role: "user", text: q, ts: Date.now() }]);
    setBusy(true);
    try {
      const r = await api.post("/support/ask", { query: q, lang });
      const d = r.data;
      const conf = Math.round((d.confidence || 0) * 100);
      setMessages((m) => [
        ...m,
        {
          role: "bot",
          text: d.answer,
          ts: Date.now(),
          confidence: conf,
          resolved: d.resolved,
          topic: d.matched_topic,
          related: d.related || [],
          fallback: d.fallback_message,
          pending_id: d.pending_id,
        },
      ]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "bot", text: (e?.response?.data?.detail || "Support engine unreachable — try again."),
          ts: Date.now(), error: true },
      ]);
    } finally { setBusy(false); }
  };

  const QUICK_CHIPS = [
    "Как вывести на Kaspi / Сбербанк?",
    "Где найти надежного P2P продавца?",
    "Какая комиссия в сети Polygon?",
  ];

  // STEP 20 — Merchant-only chip deck (surfaces only for retail/enterprise tiers)
  const MERCHANT_CHIPS = [
    "Как настроить Turbo-Zarplata?",
    "Какие налоги по ISO 20022?",
    "Как включить эквайринг 0.4%?",
  ];
  const isMerchant = accountType === "MERCHANT_RETAIL" || accountType === "MERCHANT_ENTERPRISE";

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button data-testid="turbo-ai-launcher"
          onClick={() => setOpen(true)}
          style={{
            position: "fixed", bottom: 24, right: 24, zIndex: 900,
            width: 60, height: 60, borderRadius: 30,
            background: "linear-gradient(135deg, #00F2FF, #BC13FE)",
            border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 12px 30px rgba(0,242,255,0.35), 0 0 0 4px rgba(0,242,255,0.10)",
          }}>
          <Bot size={28} color="#050914" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div data-testid="turbo-ai-panel"
          style={{
            position: "fixed", bottom: 24, right: 24, zIndex: 900,
            width: 420, maxWidth: "calc(100vw - 40px)", height: 560, maxHeight: "calc(100vh - 60px)",
            background: "linear-gradient(180deg, #0B1220 0%, #050914 100%)",
            border: "1px solid rgba(0,242,255,0.35)", borderRadius: 16,
            boxShadow: "0 20px 60px rgba(0,242,255,0.20)",
            display: "flex", flexDirection: "column", overflow: "hidden",
          }}>
          {/* Header */}
          <div style={{
            padding: "14px 16px",
            background: "linear-gradient(90deg, rgba(0,242,255,0.15), rgba(188,19,254,0.12))",
            borderBottom: "1px solid rgba(0,242,255,0.20)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 17,
                            background: "linear-gradient(135deg, #00F2FF, #BC13FE)",
                            display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Bot size={18} color="#050914" />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#E7ECFF" }}>Turbo AI Support</div>
                <div style={{ fontSize: 10, color: "#10F4A7", display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 6, height: 6, borderRadius: 3, background: "#10F4A7" }} />
                  Online · RAG · lang={lang}
                </div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="turbo-ai-close"
              style={{ background: "transparent", border: "none", color: "#8B94B8", cursor: "pointer" }}>
              <X size={18} />
            </button>
          </div>

          {/* KB Stats mini banner */}
          {stats && (
            <div data-testid="turbo-ai-stats"
              style={{ padding: "8px 14px", background: "rgba(0,0,0,0.30)",
                       borderBottom: "1px solid rgba(0,242,255,0.10)",
                       fontSize: 10, color: "#5F6884",
                       display: "flex", justifyContent: "space-between" }}>
              <span>KB: <b style={{ color: "#00F2FF" }}>{stats.total_entries}</b> · seeded {stats.seeded} · <b style={{ color: "#10F4A7" }}>learned {stats.learned}</b></span>
              <span>threshold {Math.round(stats.confidence_threshold * 100)}%</span>
            </div>
          )}

          {/* Messages */}
          <div ref={scrollRef} data-testid="turbo-ai-messages"
            style={{ flex: 1, overflowY: "auto", padding: 14,
                     display: "flex", flexDirection: "column", gap: 10 }}>
            {messages.map((m, i) => (
              <div key={i} data-testid={`turbo-ai-msg-${m.role}`}
                style={{ maxWidth: "88%", alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                         padding: "10px 12px", borderRadius: 12,
                         background: m.role === "user"
                           ? "linear-gradient(135deg, #00F2FF22, #7C3AED22)"
                           : m.error ? "rgba(255,61,108,0.10)" : "rgba(255,255,255,0.04)",
                         border: `1px solid ${m.role === "user"
                           ? "rgba(0,242,255,0.25)"
                           : m.error ? "#FF3D6C" : "rgba(255,255,255,0.08)"}`,
                         fontSize: 13, color: "#E7ECFF", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                {m.text}
                {m.role === "bot" && typeof m.confidence === "number" && (
                  <div style={{ marginTop: 6, fontSize: 10, color: "#5F6884",
                                display: "flex", alignItems: "center", gap: 6 }}>
                    {m.resolved
                      ? <><CheckCircle2 size={10} color="#10F4A7" /><span style={{ color: "#10F4A7" }}>Resolved · {m.confidence}% confidence</span></>
                      : <><AlertCircle size={10} color="#FFD700" /><span style={{ color: "#FFD700" }}>Low confidence · {m.confidence}% · queued for admin</span></>}
                    {m.topic && <span style={{ marginLeft: 6, padding: "1px 6px", borderRadius: 6, background: "rgba(0,242,255,0.10)", color: "#00F2FF" }}>{m.topic}</span>}
                  </div>
                )}
                {m.fallback && (
                  <div style={{ marginTop: 6, fontSize: 11, color: "#8B94B8", fontStyle: "italic" }}>{m.fallback}</div>
                )}
                {m.related && m.related.length > 0 && (
                  <div style={{ marginTop: 8, fontSize: 10 }}>
                    <div style={{ color: "#5F6884", marginBottom: 3 }}>Related topics:</div>
                    {m.related.slice(0, 3).map((r, j) => (
                      <button key={j} onClick={() => setInput(r.question)}
                        data-testid="turbo-ai-related"
                        style={{ display: "block", background: "transparent", border: "1px dashed rgba(0,242,255,0.20)",
                                 color: "#00F2FF", padding: "3px 6px", borderRadius: 6, marginBottom: 3,
                                 cursor: "pointer", fontSize: 10, textAlign: "left", width: "100%" }}>
                        {r.question} <span style={{ color: "#5F6884" }}>· {Math.round(r.score * 100)}%</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {busy && (
              <div style={{ alignSelf: "flex-start", padding: "8px 12px", background: "rgba(255,255,255,0.04)", borderRadius: 12, display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#8B94B8" }}>
                <Loader2 size={12} className="spin" /> Turbo AI is thinking…
              </div>
            )}
          </div>

          {/* Merchant-only quick-reply chips (Step 20) */}
          {isMerchant && (
            <div data-testid="turbo-ai-merchant-chips"
              style={{ padding: "8px 12px 0", display: "flex", flexWrap: "wrap", gap: 6,
                       borderTop: "1px solid rgba(255,215,0,0.15)", background: "rgba(255,215,0,0.04)" }}>
              {MERCHANT_CHIPS.map((label, i) => (
                <button key={i} onClick={() => send(label)} disabled={busy}
                  data-testid={`turbo-ai-merchant-chip-${i}`}
                  style={{
                    padding: "6px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600,
                    color: busy ? "#5F6884" : "#FFD700",
                    background: busy ? "rgba(255,255,255,0.03)" : "rgba(255,215,0,0.10)",
                    border: `1px solid ${busy ? "rgba(255,255,255,0.10)" : "rgba(255,215,0,0.40)"}`,
                    cursor: busy ? "not-allowed" : "pointer",
                    transition: "all 0.18s ease", whiteSpace: "nowrap",
                  }}
                  onMouseEnter={(e) => { if (!busy) { e.currentTarget.style.background = "rgba(255,215,0,0.18)"; e.currentTarget.style.transform = "translateY(-1px)"; } }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = busy ? "rgba(255,255,255,0.03)" : "rgba(255,215,0,0.10)"; e.currentTarget.style.transform = "translateY(0)"; }}>
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* Quick-reply chips (Step 19) */}
          <div data-testid="turbo-ai-quick-chips"
            style={{ padding: "8px 12px 0", display: "flex", flexWrap: "wrap", gap: 6,
                     borderTop: "1px solid rgba(0,242,255,0.10)", background: "rgba(0,0,0,0.20)" }}>
            {QUICK_CHIPS.map((label, i) => (
              <button key={i} onClick={() => send(label)} disabled={busy}
                data-testid={`turbo-ai-chip-${i}`}
                style={{
                  padding: "6px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600,
                  color: busy ? "#5F6884" : "#00F2FF",
                  background: busy ? "rgba(255,255,255,0.03)" : "rgba(0,242,255,0.08)",
                  border: `1px solid ${busy ? "rgba(255,255,255,0.10)" : "rgba(0,242,255,0.35)"}`,
                  cursor: busy ? "not-allowed" : "pointer",
                  transition: "all 0.18s ease", whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => { if (!busy) { e.currentTarget.style.background = "rgba(0,242,255,0.16)"; e.currentTarget.style.transform = "translateY(-1px)"; } }}
                onMouseLeave={(e) => { e.currentTarget.style.background = busy ? "rgba(255,255,255,0.03)" : "rgba(0,242,255,0.08)"; e.currentTarget.style.transform = "translateY(0)"; }}>
                {label}
              </button>
            ))}
          </div>

          {/* Input */}
          <div style={{ padding: 12, borderTop: "1px solid rgba(0,242,255,0.15)",
                        background: "rgba(0,0,0,0.30)", display: "flex", gap: 8 }}>
            <input value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder={lang === "ru" ? "Спросите что-нибудь…" : "Ask anything…"}
              data-testid="turbo-ai-input"
              disabled={busy}
              style={{ flex: 1, padding: "10px 12px", borderRadius: 10,
                       background: "rgba(0,0,0,0.30)", border: "1px solid rgba(0,242,255,0.25)",
                       color: "#E7ECFF", fontSize: 13, outline: "none" }} />
            <button onClick={() => send()} disabled={busy || !input.trim()}
              data-testid="turbo-ai-send"
              style={{ padding: "10px 14px", borderRadius: 10, border: "none",
                       background: input.trim() ? "linear-gradient(135deg, #00F2FF, #BC13FE)" : "#333",
                       color: "#050914", fontWeight: 700, cursor: input.trim() ? "pointer" : "not-allowed",
                       display: "flex", alignItems: "center", gap: 4 }}>
              <Send size={14} />
            </button>
          </div>
        </div>
      )}

      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </>
  );
}
